import {
  Component,
  computed,
  ElementRef,
  inject,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import {
  ELEVATION_SCALE_WIDTH,
  SyncroEditorSlopeVisualizer,
} from '../syncro-editor-slope-visualizer/syncro-editor-slope-visualizer';
import { SyncroEditorVideoVisualizer } from '../syncro-editor-video-visualizer/syncro-editor-video-visualizer';
import { MediaBlockService } from '../../services/media-block-service';
import { SyncroEditorTimeBar } from '../syncro-editor-time-bar/syncro-editor-time-bar';
import { HoverXPosition } from '../../directives/hover-x-position.directive';
import { ColorService } from '../../services/color.service';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { TimerPipe } from '@kinomap-dev/ng-ui-lib/core';

import { TranslateModule } from '@ngx-translate/core';
@Component({
  selector: 'app-syncro-editor',
  imports: [
    TranslateModule,
    SyncroEditorVideoVisualizer,
    TimerPipe,
    SyncroEditorTimeBar,
    SyncroEditorSlopeVisualizer,
    HoverXPosition,
    IconComponent,
  ],
  templateUrl: './syncro-editor.html',
  styleUrl: './syncro-editor.scss',
})
export class SyncroEditor {
  mediaBlockService: MediaBlockService = inject(MediaBlockService);
  playerService: PlayerService = inject(PlayerService);
  colorService: ColorService = inject(ColorService);
  barPosition: WritableSignal<number | null> = signal<number | null>(null);

  hoverTime: WritableSignal<number | null> = signal<number | null>(null);
  hoverSpeed: WritableSignal<number | null> = signal<number | null>(null);
  hoverAlt: WritableSignal<number | null> = signal<number | null>(null);

  selectedTime: WritableSignal<number | null> = signal<number | null>(null);
  selectedBarLeft: Signal<string | null> = computed(() => {
    const time: number | null = this.selectedTime();
    const total: number = this.mediaBlockService.effectiveTotalDuration() ?? 0;
    if (time === null || !total || !this.mediaBlockService.hasVideoAndGps()) return null;
    const inset: number = this.selectionInset;
    return `calc(${inset}px + ${time / total} * (100% - ${inset * 2}px))`;
  });

  // Icons Ref
  slopeIconRef = viewChild.required('slopeIcon', { read: ElementRef });
  videoIconRef = viewChild.required('videoIcon', { read: ElementRef });
  // mouvementIconRef = viewChild.required('mouvementIcon', { read: ElementRef })
  timeIconRef = viewChild.required('timeIcon', { read: ElementRef });

  // Charts Ref
  slopeVisualiserRef = viewChild.required('slopeVisualiser', { read: ElementRef });
  videoVisualiserRef = viewChild.required('videoVisualiser', { read: ElementRef });
  // mouvementVisualiserRef = viewChild.required('mouvementVisualiser', { read: ElementRef })
  timeVisualiserRef = viewChild.required('timeVisualiser', { read: ElementRef });
  hoverableSectionRef = viewChild.required('hoverableSection', { read: ElementRef });

  readonly tooltip: string = 'je suis un tooltip !';
  readonly elevationScaleWidth: number = ELEVATION_SCALE_WIDTH;
  /** Marge gauche/droite réservée à la bordure de la sélection : ni la barre ni le chart n'y vont. */
  readonly selectionInset: number = 8;

  changeBarPostion(barPosition: number | null) {
    if (!this.mediaBlockService.hasVideoAndGps()) return;
    const containerWidth: number = this.hoverableSectionRef().nativeElement.offsetWidth;
    const inset: number = this.selectionInset;
    // On borne la position à la zone du graphe : hors marges de la sélection et hors
    // échelle d'altitude (qui déborde à droite de la zone survolable).
    if (barPosition !== null) {
      barPosition = Math.min(Math.max(barPosition, inset), containerWidth - inset);
    }
    this.barPosition.set(barPosition);

    if (barPosition === null) {
      this.hoverTime.set(null);
      this.hoverSpeed.set(null);
      this.hoverAlt.set(null);
      return;
    }

    const ratio: number = (barPosition - inset) / (containerWidth - inset * 2);
    const relativeTime: number = ratio * (this.mediaBlockService.effectiveTotalDuration() ?? 0);

    const coords: Coordinate[] = this.mediaBlockService.syncedCoordinates();
    if (!coords.length) return;

    const targetTime: number = coords[0].coo_time + relativeTime;
    const closest: Coordinate = coords.reduce((prev, curr) =>
      Math.abs(curr.coo_time - targetTime) < Math.abs(prev.coo_time - targetTime) ? curr : prev,
    );

    this.hoverTime.set(relativeTime);
    this.hoverSpeed.set(closest.speed ?? null);
    this.hoverAlt.set(closest.elevation ?? null);
  }

  setPlayerProgress(): void {
    const hoverTime = this.hoverTime();
    if (hoverTime === null) return;
    const syncOffset = this.mediaBlockService.syncOffsetSeconds();
    const videoDuration = this.mediaBlockService.videoFileData()?.duration ?? 0;
    const videoTime = hoverTime - syncOffset;
    if (videoTime < 0 || videoTime > videoDuration) return;
    this.selectedTime.set(hoverTime);
    this.playerService.updateProgress(videoTime);
  }

  private syncIconHeight(icon: ElementRef, visualizer: ElementRef) {
    const setHeight = () => {
      icon.nativeElement.style.height = visualizer.nativeElement.offsetHeight + 'px';
    };
    const resizeObserver: ResizeObserver = new ResizeObserver(setHeight);
    resizeObserver.observe(visualizer.nativeElement);
  }

  ngAfterViewInit(): void {
    this.syncIconHeight(this.timeIconRef(), this.timeVisualiserRef());
    // this.syncIconHeight(this.mouvementIconRef(), this.mouvementVisualiserRef());
    this.syncIconHeight(this.videoIconRef(), this.videoVisualiserRef());
    this.syncIconHeight(this.slopeIconRef(), this.slopeVisualiserRef());
  }
}
